import React, { useEffect, useState } from "react";
import { Building2, Users, Ticket, ShieldCheck, Plus, Trash2, Loader2, X, Copy, Check, ExternalLink } from "lucide-react";
import { AppHeader } from "../components/AppHeader";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function SuperAdmin() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdAdminCred, setCreatedAdminCred] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/super-admin/stats");
    setData(data);
  };

  useEffect(() => { load(); }, []);

  const deleteTenant = async (tenant) => {
    if (!window.confirm(`${t("confirm_delete")}\n${tenant.name}`)) return;
    setErr("");
    try {
      await api.delete(`/super-admin/tenants/${tenant.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader activeItem="super" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-blue-700 font-medium">{t("super_admin")}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">{t("platform_stats")}</h1>
          </div>
          <button
            onClick={() => { setShowCreate(true); setCreatedAdminCred(null); setErr(""); }}
            data-testid="super-create-tenant-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-700 text-white font-medium hover:bg-blue-800"
          >
            <Plus className="w-4 h-4" /> {t("new_tenant")}
          </button>
        </div>

        <div className="mb-6 inline-flex items-center gap-2 bg-amber-50 text-amber-900 border border-amber-100 rounded-xl px-4 py-2 text-sm">
          <ShieldCheck className="w-4 h-4" /> {t("no_patient_details_notice")}
        </div>

        {err && <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="super-error">{err}</div>}
        {createdAdminCred && <CreatedAdminBanner cred={createdAdminCred} onDismiss={() => setCreatedAdminCred(null)} />}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Building2} label={t("tenants")} value={data?.summary.tenants ?? 0} tone="bg-blue-50 text-blue-900 border-blue-100" />
          <StatCard icon={Users} label={t("users")} value={data?.summary.users ?? 0} tone="bg-violet-50 text-violet-900 border-violet-100" />
          <StatCard icon={Ticket} label={t("tickets_today")} value={data?.summary.tickets_today ?? 0} tone="bg-emerald-50 text-emerald-900 border-emerald-100" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="p-5 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-lg">{t("tenants")}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-[0.15em] text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-5 py-3">{t("clinic")}</th>
                  <th className="px-5 py-3">{t("slug")}</th>
                  <th className="px-5 py-3">{t("users")}</th>
                  <th className="px-5 py-3">{t("tickets_today")}</th>
                  <th className="px-5 py-3 text-right">{t("actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="super-tenants-table">
                {(data?.tenants || []).map((tn) => (
                  <tr key={tn.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 font-medium text-slate-900">
                        {tn.name}
                        {tn.is_demo && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-amber-100 text-amber-800">{t("demo")}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <a href={`/kiosk/${tn.slug}`} target="_blank" rel="noopener noreferrer" className="font-mono text-slate-600 hover:text-blue-700 inline-flex items-center gap-1">
                        {tn.slug} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{tn.users}</td>
                    <td className="px-5 py-3 text-slate-700">{tn.tickets_today}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => deleteTenant(tn)}
                        disabled={tn.is_demo}
                        data-testid={`super-delete-${tn.slug}`}
                        title={tn.is_demo ? t("demo_tenant_locked") : t("delete")}
                        className="p-2 rounded-full hover:bg-rose-50 text-rose-700 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={(res) => {
            setShowCreate(false);
            setCreatedAdminCred({ ...res.admin, tenant: res.tenant });
            load();
          }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className={`rounded-2xl border p-5 ${tone}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] opacity-80">{label}</div>
        <Icon className="w-5 h-5 opacity-70" />
      </div>
      <div className="font-heading text-4xl font-bold mt-2">{value}</div>
    </div>
  );
}

function CreatedAdminBanner({ cred, onDismiss }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copyAll = () => {
    const txt = `${cred.email} / ${cred.temp_password}`;
    navigator.clipboard.writeText(txt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="super-created-cred">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-heading font-semibold text-emerald-900 text-lg flex items-center gap-2">
            <Check className="w-5 h-5" /> {t("tenant_created")} — {cred.tenant?.name}
          </div>
          <div className="mt-2 text-sm text-emerald-900">
            {t("admin_email")} : <code className="font-mono bg-white px-2 py-0.5 rounded">{cred.email}</code>
          </div>
          <div className="mt-1 text-sm text-emerald-900">
            {t("temp_password")} : <code className="font-mono bg-white px-2 py-0.5 rounded font-bold">{cred.temp_password}</code>
          </div>
          <div className="mt-2 text-xs text-emerald-800">
            Kiosk : <a href={`/kiosk/${cred.tenant?.slug}`} className="underline" target="_blank" rel="noopener noreferrer">/kiosk/{cred.tenant?.slug}</a>
            {" · "}
            Display : <a href={`/display/${cred.tenant?.slug}`} className="underline" target="_blank" rel="noopener noreferrer">/display/{cred.tenant?.slug}</a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyAll} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-900 text-sm hover:bg-emerald-100" data-testid="super-copy-cred">
            <Copy className="w-3 h-3" /> {copied ? t("copied") : t("copy")}
          </button>
          <button onClick={onDismiss} className="p-1.5 rounded-full hover:bg-emerald-100 text-emerald-700"><X className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function CreateTenantModal({ onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    slug: "",
    address: "",
    phone: "",
    admin_email: "",
    admin_name: "",
    admin_password: "",
    seed_defaults: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = { ...form };
      if (!body.admin_password) delete body.admin_password;
      if (!body.slug) delete body.slug;
      if (!body.address) delete body.address;
      if (!body.phone) delete body.phone;
      const { data } = await api.post("/super-admin/tenants", body);
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="super-create-modal">
      <div className="w-full max-w-2xl bg-white rounded-3xl border border-slate-200 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-heading text-xl font-semibold">{t("create_tenant")}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{t("seed_defaults_label")}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 grid sm:grid-cols-2 gap-4">
          <Field label={t("tenant_name")} required>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Clinique du Soleil" data-testid="super-input-name" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </Field>
          <Field label={t("slug")} hint="(auto)">
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="clinique-du-soleil" data-testid="super-input-slug" className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm" />
          </Field>
          <Field label={t("tenant_address")}>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="super-input-address" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </Field>
          <Field label={t("tenant_phone")}>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="super-input-phone" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </Field>
          <Field label={t("admin_email")} required>
            <input required type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@clinique.com" data-testid="super-input-admin-email" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </Field>
          <Field label={t("admin_name")} required>
            <input required value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="Dr. Diop" data-testid="super-input-admin-name" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("auto_password")}>
              <input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="super-input-admin-password" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </Field>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2 pt-1">
            <input id="seed-defaults" type="checkbox" checked={form.seed_defaults} onChange={(e) => setForm({ ...form, seed_defaults: e.target.checked })} data-testid="super-input-seed-defaults" className="h-4 w-4" />
            <label htmlFor="seed-defaults" className="text-sm text-slate-700">{t("seed_defaults_label")}</label>
          </div>

          {err && <div className="sm:col-span-2 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="super-create-error">{err}</div>}

          <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 hover:bg-slate-50">{t("cancel")}</button>
            <button type="submit" disabled={busy} data-testid="super-create-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t("create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700 uppercase tracking-wider">
        {label} {required && <span className="text-rose-500">*</span>} {hint && <span className="text-slate-400 normal-case">{hint}</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
